import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    // Load cart from localStorage on initial render
    const savedCart = localStorage.getItem('gumba_cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('gumba_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (equipment, quantity = 1, dates = null) => {
    setCartItems(prev => {
      // Check if item already exists in cart
      const existingIndex = prev.findIndex(item => item.equipment_id === equipment.equipment_id);
      
      if (existingIndex >= 0) {
        // Update quantity if item exists
        const updated = [...prev];
        const maxQty = equipment.quantity_available || 10;
        updated[existingIndex].quantity = Math.min(
          updated[existingIndex].quantity + quantity,
          maxQty
        );
        if (dates) {
          updated[existingIndex].dates = dates;
        }
        return updated;
      }
      
      // Add new item
      return [...prev, {
        equipment_id: equipment.equipment_id,
        name: equipment.name,
        price: equipment.price,
        image_url: equipment.image_url || equipment.image,
        category: equipment.category,
        business_id: equipment.business_id,
        business_name: equipment.business_name,
        quantity_available: equipment.quantity_available,
        quantity: quantity,
        dates: dates
      }];
    });
  };

  const removeFromCart = (equipmentId) => {
    setCartItems(prev => prev.filter(item => item.equipment_id !== equipmentId));
  };

  const updateQuantity = (equipmentId, quantity) => {
    if (quantity < 1) {
      removeFromCart(equipmentId);
      return;
    }
    
    setCartItems(prev => prev.map(item => {
      if (item.equipment_id === equipmentId) {
        return {
          ...item,
          quantity: Math.min(quantity, item.quantity_available || 10)
        };
      }
      return item;
    }));
  };

  const updateDates = (equipmentId, dates) => {
    setCartItems(prev => prev.map(item => {
      if (item.equipment_id === equipmentId) {
        return { ...item, dates };
      }
      return item;
    }));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      const days = item.dates?.duration || 1;
      return total + (item.price * item.quantity * days);
    }, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const isInCart = (equipmentId) => {
    return cartItems.some(item => item.equipment_id === equipmentId);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateDates,
    clearCart,
    getCartTotal,
    getCartCount,
    isInCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
